import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/ui/Card';
import { ToggleStatCard } from '../components/ui/ToggleStatCard';
import { Button } from '../components/ui/Button';
import { ActivityCard, type Activity } from '../components/executive-protection/ActivityCard';
import { executiveProtectionService, type Activity as APIActivity } from '../services/executive-protection.service';

type ActivityStatus = 'ALL' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
type DateRange = 'TODAY' | 'UPCOMING' | 'PAST' | 'ALL';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Activities() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params with defaults
  const [statusFilter, setStatusFilter] = useState<ActivityStatus>(
    (searchParams.get('status') as ActivityStatus) || 'PLANNED'
  );
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>(
    (searchParams.get('dateRange') as DateRange) || 'TODAY'
  );

  // Sync URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'PLANNED') {
      params.set('status', statusFilter);
    }
    if (dateRangeFilter !== 'TODAY') {
      params.set('dateRange', dateRangeFilter);
    }
    setSearchParams(params, { replace: true });
  }, [statusFilter, dateRangeFilter, setSearchParams]);

  // Query with filters
  const { data: activities = [], isLoading, error } = useQuery({
    queryKey: ['executive-protection', 'activities', 'filtered', { status: statusFilter, dateRange: dateRangeFilter }],
    queryFn: () => executiveProtectionService.getFilteredActivities({
      status: statusFilter,
      dateRange: dateRangeFilter,
    }),
    staleTime: 240000,
    refetchInterval: 300000,
  });

  // Map API activities to component format
  const mappedActivities: Activity[] = activities.map((activity: APIActivity) => ({
    ...activity,
    personId: activity.protectedPersonId,
    personName: activity.protectedPersonName ?? '',
    riskAssessment: activity.riskAssessment as 'low' | 'medium' | 'high' | undefined,
  }));

  // Count stats for display
  const plannedCount = activities.filter((a: APIActivity) => a.status.toUpperCase() === 'PLANNED').length;
  const inProgressCount = activities.filter((a: APIActivity) => a.status.toUpperCase() === 'IN_PROGRESS').length;
  const completedCount = activities.filter((a: APIActivity) => a.status.toUpperCase() === 'COMPLETED').length;
  const cancelledCount = activities.filter((a: APIActivity) => a.status.toUpperCase() === 'CANCELLED').length;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div>
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
            All Activities
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400">
            Manage and view all executive protection activities
          </p>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="w-5 h-5 text-secondary-500" />
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Filters:</span>
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <label htmlFor="status-filter" className="text-sm text-secondary-600 dark:text-secondary-400">
                  Status:
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ActivityStatus)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="PLANNED">Planned</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="ALL">All Status</option>
                </select>
              </div>

              {/* Date Range Filter */}
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4 text-secondary-500" />
                <label htmlFor="date-filter" className="text-sm text-secondary-600 dark:text-secondary-400">
                  Date:
                </label>
                <select
                  id="date-filter"
                  value={dateRangeFilter}
                  onChange={(e) => setDateRangeFilter(e.target.value as DateRange)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="TODAY">Today</option>
                  <option value="UPCOMING">Upcoming (30 days)</option>
                  <option value="PAST">Past (30 days)</option>
                  <option value="ALL">All Dates</option>
                </select>
              </div>

              {/* Results count */}
              <div className="ml-auto text-sm text-secondary-600 dark:text-secondary-400">
                {isLoading ? 'Loading...' : `${mappedActivities.length} activities found`}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats Summary */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ToggleStatCard
          label="Planned"
          active={statusFilter === 'PLANNED'}
          onToggle={() => setStatusFilter(statusFilter === 'PLANNED' ? 'ALL' : 'PLANNED')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{plannedCount}</div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Planned</div>
          </CardContent>
        </ToggleStatCard>
        <ToggleStatCard
          label="In Progress"
          active={statusFilter === 'IN_PROGRESS'}
          activeRingClass="ring-warning-500"
          onToggle={() => setStatusFilter(statusFilter === 'IN_PROGRESS' ? 'ALL' : 'IN_PROGRESS')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">{inProgressCount}</div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">In Progress</div>
          </CardContent>
        </ToggleStatCard>
        <ToggleStatCard
          label="Completed"
          active={statusFilter === 'COMPLETED'}
          activeRingClass="ring-success-500"
          onToggle={() => setStatusFilter(statusFilter === 'COMPLETED' ? 'ALL' : 'COMPLETED')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">{completedCount}</div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Completed</div>
          </CardContent>
        </ToggleStatCard>
        <ToggleStatCard
          label="Cancelled"
          active={statusFilter === 'CANCELLED'}
          activeRingClass="ring-secondary-500"
          onToggle={() => setStatusFilter(statusFilter === 'CANCELLED' ? 'ALL' : 'CANCELLED')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">{cancelledCount}</div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Cancelled</div>
          </CardContent>
        </ToggleStatCard>
      </motion.div>

      {/* Activities Grid */}
      <motion.div variants={itemVariants}>
        {isLoading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-secondary-500 dark:text-secondary-400">Loading activities...</div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-danger-600 dark:text-danger-400">Error loading activities. Please try again.</div>
            </CardContent>
          </Card>
        ) : mappedActivities.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-secondary-400" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No activities found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {statusFilter !== 'ALL' || dateRangeFilter !== 'ALL'
                  ? 'Try adjusting your filters to see more activities.'
                  : 'Create your first activity to get started.'}
              </p>
              {(statusFilter !== 'ALL' || dateRangeFilter !== 'ALL') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setStatusFilter('ALL');
                    setDateRangeFilter('ALL');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mappedActivities.map((activity) => (
              <motion.div key={activity.id} variants={itemVariants} layout>
                <ActivityCard activity={activity} />
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
